import {
  AdminApiError,
  type AdminClient,
  type JackpotCategoryPoolView,
  type JackpotDetailResponse,
  type JackpotDrainRow,
  type JackpotGameSummary,
} from '@apx/admin-sdk';
import { fmtDateTime, fmtMoney } from '../format.js';
import { emptyState } from '../empty-state.js';
import { prettyGame } from '../games.js';

export interface JackpotOverviewDeps {
  client: AdminClient;
}

type Window = '1h' | '24h' | '7d' | '30d';

const WINDOWS: ReadonlyArray<{ value: Window; label: string }> = [
  { value: '1h', label: 'Last hour' },
  { value: '24h', label: '24 hours' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
];

/** Human label for the jackpot model, shown in the summary table. */
function modelLabel(model: JackpotGameSummary['model']): string {
  switch (model) {
    case 'category':
      return 'Category (veg/meat)';
    case 'triple-drain':
      return 'Triple-drain';
    case 'none':
      return '—';
  }
}

export async function renderJackpotOverviewPage(
  root: HTMLElement,
  deps: JackpotOverviewDeps,
): Promise<void> {
  root.innerHTML = `
    <h1 class="page-title">Jackpot pool</h1>
    <p class="page-sub">Provider-scope view. <strong>Scope varies per game</strong> — greedyking is a single global pot shared across all integrators, sicbo is per-integrator (each integrator has its own pot; the balance shown is the aggregate), and Dragon Tiger, Singapore Lottery and Baccarat have none. Jackpot config is set once by apx admins and applies across all integrators. Each game has its own jackpot model — see per-row detail below.</p>

    <div class="card">
      <h2>Pool status per game</h2>
      <div id="jp-list"><p class="muted">Loading…</p></div>
    </div>

    <div class="card">
      <h2>Detail</h2>
      <div class="jp-controls">
        <div class="jp-control">
          <span class="jp-control-label">Game</span>
          <div class="rtp-subtabs jp-seg" id="jp-games"></div>
        </div>
        <div class="jp-control">
          <span class="jp-control-label">Window</span>
          <div class="rtp-subtabs jp-seg" id="jp-windows">
            ${WINDOWS.map(
              (w) =>
                `<button class="rtp-subtab${w.value === '24h' ? ' active' : ''}" data-window="${w.value}">${w.label}</button>`,
            ).join('')}
          </div>
        </div>
        <span id="jp-msg" class="muted"></span>
      </div>
      <div id="jp-detail"><p class="muted">Pick a game to see contributions + wins.</p></div>
    </div>
  `;

  const listEl = root.querySelector<HTMLElement>('#jp-list')!;
  const detailEl = root.querySelector<HTMLElement>('#jp-detail')!;
  const gamesEl = root.querySelector<HTMLElement>('#jp-games')!;
  const windowsEl = root.querySelector<HTMLElement>('#jp-windows')!;
  const msgEl = root.querySelector<HTMLElement>('#jp-msg')!;

  const state: { gameId: string; window: Window } = { gameId: '', window: '24h' };

  function markActive(container: HTMLElement, attr: string, value: string): void {
    container.querySelectorAll<HTMLButtonElement>('.rtp-subtab').forEach((btn) => {
      btn.classList.toggle('active', btn.dataset[attr] === value);
    });
  }

  async function loadSummary(): Promise<void> {
    try {
      const { games } = await deps.client.fetchJackpotSummary();
      listEl.innerHTML = renderSummary(games);
      // Detail below is fetched per gameId (not per currency yet), so the
      // game selector dedupes by gameId even if the summary table now shows
      // one row per (game, currency).
      const uniqueGameIds = [...new Set(games.map((g) => g.gameId))];
      gamesEl.innerHTML = uniqueGameIds
        .map(
          (gameId) =>
            `<button class="rtp-subtab" data-game="${gameId}">${escapeHtml(prettyGame(gameId))}</button>`,
        )
        .join('');
      gamesEl.querySelectorAll<HTMLButtonElement>('.rtp-subtab').forEach((btn) => {
        btn.addEventListener('click', () => {
          state.gameId = btn.dataset.game!;
          markActive(gamesEl, 'game', state.gameId);
          void loadDetail();
        });
      });
      if (games[0]) {
        state.gameId = games[0].gameId;
        markActive(gamesEl, 'game', state.gameId);
        await loadDetail();
      }
    } catch (err) {
      listEl.innerHTML = `<p class="error">${escapeHtml(describe(err))}</p>`;
    }
  }

  async function loadDetail(): Promise<void> {
    if (!state.gameId) {
      detailEl.innerHTML = '<p class="muted">Pick a game to see contributions + wins.</p>';
      return;
    }
    detailEl.innerHTML = '<p class="muted">Loading…</p>';
    msgEl.textContent = '';
    try {
      // Detail (pool + contributions) and the canonical win history (drains)
      // load in parallel — the latter is the real "jackpot hit" record.
      const [data, drainsRes] = await Promise.all([
        deps.client.fetchJackpotDetail(state.gameId, { since: state.window, limit: 100 }),
        deps.client.fetchJackpotDrains(state.gameId, { since: state.window, limit: 50 }),
      ]);
      detailEl.innerHTML = renderDetail(data, drainsRes.drains);
    } catch (err) {
      detailEl.innerHTML = `<p class="error">${escapeHtml(describe(err))}</p>`;
    }
  }

  windowsEl.querySelectorAll<HTMLButtonElement>('.rtp-subtab').forEach((btn) => {
    btn.addEventListener('click', () => {
      state.window = btn.dataset.window as Window;
      markActive(windowsEl, 'window', state.window);
      void loadDetail();
    });
  });

  function renderSummary(games: JackpotGameSummary[]): string {
    if (games.length === 0) {
      return '<p class="muted">No rounds have settled yet — jackpot pool is empty.</p>';
    }
    return `<table>
      <thead><tr>
        <th>Game</th>
        <th>Currency</th>
        <th>Model</th>
        <th>Pool</th>
        <th>Contribution</th>
      </tr></thead>
      <tbody>${games.map((g) => renderSummaryRow(g)).join('')}</tbody>
    </table>`;
  }

  function renderSummaryRow(g: JackpotGameSummary): string {
    const gameCell = `<td><code>${escapeHtml(g.gameId)}</code></td>`;
    if (g.model === 'none') {
      return `<tr>
        ${gameCell}
        <td colspan="4">${emptyState({
          icon: 'plug',
          title: `${prettyGame(g.gameId)} has no jackpot pool`,
          hint: "This game doesn't contribute to or pay a jackpot.",
        })}</td>
      </tr>`;
    }
    // Currency is per-pool — a row with no `currency` predates the
    // native-currency split and reads as the IDR pool.
    const cur = g.currency ?? 'IDR';
    const currencyCell = `<td><code>${escapeHtml(cur)}</code></td>`;
    if (g.model === 'triple-drain') {
      const balance = Number(g.pool.balance);
      const min = Number(g.pool.minDrainBalanceCredits);
      // Per-integrator (aggregate) pools have no single arm/fill state — the
      // summed balance vs a per-integrator min is meaningless. Show the scope
      // note instead of a bar + "armed" chip; per-integrator arm state lives
      // on the integrator jackpot view.
      if (g.aggregate) {
        return `<tr>
        ${gameCell}
        ${currencyCell}
        <td>${modelLabel(g.model)}</td>
        <td>
          <div>${fmtMoney(g.pool.balance, cur)} total · min ${fmtMoney(g.pool.minDrainBalanceCredits, cur)} per integrator</div>
          <div class="muted" style="font-size:11px">${escapeHtml(
            g.note ?? 'per-integrator pools (aggregate)',
          )} — arm state is per integrator</div>
        </td>
        <td>${(g.pool.contributionRate * 100).toFixed(2)}% of stake</td>
      </tr>`;
      }
      const fillPct = min > 0 ? Math.min(1, balance / min) : 1;
      return `<tr>
        ${gameCell}
        ${currencyCell}
        <td>${modelLabel(g.model)}</td>
        <td>
          <div>${fmtMoney(g.pool.balance, cur)} · min ${fmtMoney(g.pool.minDrainBalanceCredits, cur)}${armedChip(fillPct)}</div>
          ${renderBar(fillPct, 'var(--accent-2)')}
          <div class="muted" style="font-size:11px">Fires on every triple once the pool is at/over the minimum drain balance</div>
        </td>
        <td>${(g.pool.contributionRate * 100).toFixed(2)}% of stake</td>
      </tr>`;
    }
    // category
    const [veg, meat] = g.pools;
    return `<tr>
      ${gameCell}
      ${currencyCell}
      <td>${modelLabel(g.model)}</td>
      <td>
        ${renderCategoryCell('SALAD (veg)', veg, 'var(--accent-2)', cur)}
        ${renderCategoryCell('PIZZA (meat)', meat, 'var(--accent)', cur)}
      </td>
      <td>${(g.contributionRateBps / 100).toFixed(2)}% of losing-bet house take (veg rate shown)</td>
    </tr>`;
  }

  /** `cur` is the pool's currency: its balance, and the threshold it is
   *  measured against, are integer minor units of it. */
  function renderCategoryCell(label: string, pool: JackpotCategoryPoolView, color: string, cur: string): string {
    return `<div style="margin-bottom:6px">
      <div>${label}: ${fmtMoney(pool.balance, cur)} / ${fmtMoney(pool.threshold, cur)} · ${(pool.fillPct * 100).toFixed(1)}%${armedChip(pool.fillPct)}</div>
      ${renderBar(pool.fillPct, color)}
      <div class="muted" style="font-size:11px">up to ${(pool.maxTriggerRateBps / 100).toFixed(2)}% fire chance at full pool</div>
    </div>`;
  }

  // Fill progress bar. "Armed" (pool at/over threshold/min-drain) gets a
  // glow + subtle pulse so admins spot a live jackpot at a glance.
  function renderBar(pct: number, color: string): string {
    const w = Math.max(0, Math.min(100, pct * 100));
    const armed = pct >= 1;
    return `<div class="jp-bar${armed ? ' jp-bar--armed' : ''}" style="--jp-bar-color:${color}">
      <div class="jp-bar__fill" style="width:${w.toFixed(1)}%"></div>
    </div>`;
  }

  function armedChip(pct: number): string {
    return pct >= 1 ? ' <span class="jp-armed">armed</span>' : '';
  }

  function renderModelKpis(d: JackpotDetailResponse): string {
    if (d.model === 'none') return '';
    // The detail is scoped to one pool currency; every amount below is minor
    // units of it.
    const cur = d.currency ?? 'IDR';
    if (d.model === 'triple-drain') {
      const balance = Number(d.pool.balance);
      const min = Number(d.pool.minDrainBalanceCredits);
      // Aggregate (per-integrator) pools: no single fill/arm state. Surface the
      // integrator count instead of a "% filled" figure that would compare a
      // cross-integrator sum against a per-integrator threshold.
      if (d.aggregate) {
        return `
        ${kpi(`Total pool balance (${escapeHtml(cur)})`, fmtMoney(d.pool.balance, cur), `across ${d.integratorCount ?? 0} integrator${d.integratorCount === 1 ? '' : 's'}`)}
        ${kpi('Min drain balance', fmtMoney(d.pool.minDrainBalanceCredits, cur), 'per integrator')}
        ${kpi('Contribution rate', `${(d.pool.contributionRate * 100).toFixed(2)}%`, 'of stake')}
      `;
      }
      const fillPct = min > 0 ? Math.min(1, balance / min) : 1;
      return `
        ${kpi(`Pool balance (${escapeHtml(cur)})`, fmtMoney(d.pool.balance, cur))}
        ${kpi('Min drain balance', fmtMoney(d.pool.minDrainBalanceCredits, cur), `${(fillPct * 100).toFixed(1)}% filled`)}
        ${kpi('Contribution rate', `${(d.pool.contributionRate * 100).toFixed(2)}%`, 'of stake')}
      `;
    }
    const [veg, meat] = d.pools;
    return `
      ${kpi(`Veg pool (SALAD, ${escapeHtml(cur)})`, fmtMoney(veg.balance, cur), `${(veg.fillPct * 100).toFixed(1)}% of ${fmtMoney(veg.threshold, cur)}`)}
      ${kpi(`Meat pool (PIZZA, ${escapeHtml(cur)})`, fmtMoney(meat.balance, cur), `${(meat.fillPct * 100).toFixed(1)}% of ${fmtMoney(meat.threshold, cur)}`)}
      ${kpi('Contribution rate', `${(d.contributionRateBps / 100).toFixed(2)}%`, 'of losing-bet house take (veg rate shown)')}
    `;
  }

  function renderDetail(d: JackpotDetailResponse, drains: JackpotDrainRow[]): string {
    const infoBlock =
      d.model === 'none'
        ? `<div class="card" style="margin:0 0 14px">${emptyState({
            icon: 'plug',
            title: `${prettyGame(d.gameId)} has no jackpot pool`,
            hint: "This game doesn't contribute to or pay a jackpot — there's nothing to drain or configure here. The rounds/house-take KPIs below still reflect real play.",
          })}</div>`
        : '';

    // Window totals and contribution rows are the detail currency's own
    // partition of each round, so they share its unit.
    const cur = d.currency ?? 'IDR';
    return `
      ${infoBlock}
      <div class="kpi-grid">
        ${renderModelKpis(d)}
        ${kpi(`Rounds (${d.window})`, d.windowSummary.rounds.toLocaleString())}
        ${kpi(`House take (${d.window}, ${escapeHtml(cur)})`, fmtMoney(d.windowSummary.totalHouseTake, cur))}
        ${d.model !== 'none' ? kpi(`Contribution (${d.window}, ${escapeHtml(cur)})`, fmtMoney(d.windowSummary.totalContribution, cur), 'added to pool') : ''}
      </div>

      ${(() => {
        const total = d.windowSummary.contributingRounds ?? d.recentContributions.length;
        const shown = d.recentContributions.length;
        const heading =
          total > shown
            ? `Recent contributions (${shown} of ${total.toLocaleString()} contributing rounds)`
            : `Recent contributions (${shown} contributing ${shown === 1 ? 'round' : 'rounds'})`;
        return `
      <h3 style="margin:18px 0 4px">${heading}</h3>
      <p class="muted" style="font-size:12px;margin:0 0 8px">Only rounds whose house take funded the pool. Click a row for its full round trace.</p>
      ${
        shown === 0
          ? '<p class="muted">No rounds contributed to the pool in this window (no net house take).</p>'
          : `<div style="max-height:50vh;overflow:auto"><table>
              <thead><tr>
                <th>Ended</th><th>Integrator</th><th>Round</th>
                <th style="text-align:right">Total bet (${escapeHtml(cur)})</th>
                <th style="text-align:right">Payout</th>
                <th style="text-align:right">House take</th>
                <th style="text-align:right">Contribution</th>
                <th style="text-align:right">Plyrs</th>
              </tr></thead>
              <tbody>${d.recentContributions
                .map(
                  (c) => `<tr class="row-nav" data-row-href="/integrators/${encodeURIComponent(c.integratorId)}/transaction-trace?id=${encodeURIComponent(c.roundId)}" title="Open round trace">
                    <td>${c.endedAt ? fmtDateTime(c.endedAt) : '—'}</td>
                    <td><code>${c.integratorId.slice(0, 8)}…</code></td>
                    <td><code>${c.roundId.slice(-8)}</code></td>
                    <td style="text-align:right">${fmtMoney(c.totalBet, cur)}</td>
                    <td style="text-align:right">${fmtMoney(c.totalPayout, cur)}</td>
                    <td style="text-align:right">${fmtMoney(c.houseTake, cur)}</td>
                    <td style="text-align:right;color:var(--accent);font-weight:600">${fmtMoney(c.contribution, cur)}</td>
                    <td style="text-align:right">${c.participantCount}</td>
                  </tr>`,
                )
                .join('')}</tbody>
            </table></div>`
      }`;
      })()}

      <h3 style="margin:18px 0 4px">Recent jackpot wins (${drains.length})</h3>
      <p class="muted" style="font-size:12px;margin:0 0 8px">Actual pool payouts — a player hit the jackpot and drained the pot. Click a row for the winning round's trace.</p>
      ${
        drains.length === 0
          ? '<p class="muted">No jackpot wins in this window — the pool has not been hit.</p>'
          : `<div style="max-height:50vh;overflow:auto"><table>
              <thead><tr>
                <th>Won at</th><th>Integrator</th><th>Player</th><th>Round</th>
                <th>Currency</th><th style="text-align:right">Amount</th><th>Status</th>
              </tr></thead>
              <tbody>${drains
                .map((w) => {
                  const href = w.roundId
                    ? `/integrators/${encodeURIComponent(w.integratorId)}/transaction-trace?id=${encodeURIComponent(w.roundId)}`
                    : '';
                  const rowAttrs = href
                    ? ` class="row-nav" data-row-href="${href}" title="Open winning round trace"`
                    : '';
                  return `<tr${rowAttrs}>
                    <td>${fmtDateTime(w.createdAt)}</td>
                    <td><code>${w.integratorId.slice(0, 8)}…</code></td>
                    <td><code>${w.playerId.slice(0, 8)}…</code></td>
                    <td>${w.roundId ? `<code>${w.roundId.slice(-8)}</code>` : '<span class="muted">—</span>'}</td>
                    <td>${w.currency ? `<code>${escapeHtml(w.currency)}</code>` : '<span class="muted">—</span>'}</td>
                    <td style="text-align:right;color:var(--accent);font-weight:600">${fmtMoney(w.creditAmount, w.currency)}</td>
                    <td><span class="pill ${w.status === 'completed' || w.status === 'settled' ? 'live' : ''}">${escapeHtml(w.status)}</span></td>
                  </tr>`;
                })
                .join('')}</tbody>
            </table></div>`
      }
    `;
  }

  function kpi(label: string, value: string, sub?: string): string {
    return `<div class="kpi">
      <div class="kpi-label">${label}</div>
      <div class="kpi-value">${value}</div>
      ${sub ? `<div class="kpi-sub">${sub}</div>` : ''}
    </div>`;
  }

  await loadSummary();
}

function describe(err: unknown): string {
  if (err instanceof AdminApiError) return `${err.code}: ${err.message}`;
  return err instanceof Error ? err.message : String(err);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      case "'":
        return '&#39;';
      default:
        return c;
    }
  });
}
